import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuAnchor, LuCalendar, LuCheck, LuMap, LuPencil, LuSearch, LuShip, LuWrench, LuX } from 'react-icons/lu'
import { BerthMap, type BerthPositionPatch } from '@/frontend/components/marina/BerthMap'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type BerthPositionUpdate, useBulkBerthPosition, usePontoons } from '@/frontend/hooks/useBerths'
import { useOccupancyMap } from '@/frontend/hooks/useOccupancy'

export const Route = createFileRoute('/dashboard/occupancy/map')({
  component: BerthMapPage,
})

const SAVE_DEBOUNCE_MS = 800

// Berth status color swatches — mirrors BerthMap's COLOR_HEX
const STATUS_META = [
  { status: 'AVAILABLE', color: '#40C057' },
  { status: 'OCCUPIED', color: '#339AF0' },
  { status: 'RESERVED', color: '#12B886' },
  { status: 'MAINTENANCE', color: '#868E96' },
  { status: 'BLOCKED', color: '#FA5252' },
]

function BerthMapPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useCanWrite()
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [pontoonId, setPontoonId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [editMode, setEditMode] = useState(false)
  const dirty = useRef<Map<string, BerthPositionUpdate>>(new Map())
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const bulkSave = useBulkBerthPosition()

  const { data: pontoonsData } = usePontoons()
  const pontoons = pontoonsData?.pontoons ?? []
  const { data, isLoading } = useOccupancyMap(date)

  const allBerths = data?.berths ?? []
  const berths = allBerths.filter((b) => {
    if (pontoonId && b.pontoonId !== pontoonId) return false
    if (search && !b.code.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  // Stats from current pontoon filter context
  const statBerths = pontoonId ? allBerths.filter((b) => b.pontoonId === pontoonId) : allBerths
  const stats = {
    total: statBerths.length,
    available: statBerths.filter((b) => b.status === 'AVAILABLE').length,
    occupied: statBerths.filter((b) => b.status === 'OCCUPIED').length,
    reserved: statBerths.filter((b) => b.status === 'RESERVED').length,
    maintenance: statBerths.filter((b) => b.status === 'MAINTENANCE' || b.status === 'BLOCKED').length,
  }
  const occupancyPct = stats.total > 0 ? Math.round(((stats.occupied + stats.reserved) / stats.total) * 100) : 0
  const isToday = date === dayjs().format('YYYY-MM-DD')

  function handlePosition(p: BerthPositionPatch) {
    dirty.current.set(p.id, { id: p.id, mapX: p.mapX, mapY: p.mapY })
    setPendingCount(dirty.current.size)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS)
  }

  function flushSave() {
    if (dirty.current.size === 0) return
    const updates = Array.from(dirty.current.values())
    dirty.current.clear()
    setPendingCount(0)
    bulkSave.mutate(updates, {
      onSuccess: (res) => {
        notifications.show({
          color: 'teal',
          icon: <LuCheck size={16} />,
          message: `Tersimpan ${res.updated ?? updates.length} posisi berth`,
        })
      },
      onError: (e: any) => {
        notifications.show({
          color: 'red',
          title: 'Gagal menyimpan',
          message: e?.message ?? 'Tidak bisa menyimpan posisi berth',
        })
      },
    })
  }

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    },
    [],
  )

  function exitEditMode() {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    flushSave()
    setEditMode(false)
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuMap size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.occupancy.map.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {isToday ? 'Hari ini' : dayjs(date).format('dddd, DD MMM YYYY')}
                {pontoonId && ` · Pontoon ${pontoons.find((p) => p.id === pontoonId)?.code ?? ''}`}
              </Text>
            </div>
            {editMode && (
              <Badge color="karang" variant="light" leftSection={<LuPencil size={11} />}>
                EDIT LAYOUT
              </Badge>
            )}
            {pendingCount > 0 && (
              <Badge color="yellow" variant="dot">
                {t('page.occupancy.map.unsaved', { n: pendingCount })}
              </Badge>
            )}
            {bulkSave.isPending && (
              <Badge color="blue" variant="dot">
                {t('page.occupancy.map.saving')}
              </Badge>
            )}
          </Group>

          {canWrite && (
            <Group gap="xs">
              {editMode ? (
                <>
                  <Tooltip label="Simpan perubahan & keluar edit mode" withArrow>
                    <Button
                      color="laut"
                      leftSection={<LuCheck size={15} />}
                      onClick={exitEditMode}
                      loading={bulkSave.isPending}
                      size="sm"
                    >
                      {t('page.occupancy.map.btn_done')}
                    </Button>
                  </Tooltip>
                  <Button
                    variant="default"
                    leftSection={<LuX size={15} />}
                    size="sm"
                    onClick={() => {
                      dirty.current.clear()
                      setPendingCount(0)
                      setEditMode(false)
                    }}
                  >
                    {t('page.occupancy.map.btn_cancel')}
                  </Button>
                </>
              ) : (
                <Tooltip label="Drag berth untuk mengatur posisi di peta" withArrow>
                  <Button
                    variant="light"
                    color="gray"
                    leftSection={<LuPencil size={15} />}
                    size="sm"
                    onClick={() => setEditMode(true)}
                  >
                    {t('page.occupancy.map.btn_edit')}
                  </Button>
                </Tooltip>
              )}
            </Group>
          )}
        </Group>

        {/* Occupancy stat strip */}
        {!isLoading && stats.total > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <StatChip
              icon={<LuAnchor size={15} />}
              label={t('page.occupancy.map.stat_available')}
              value={stats.available}
              total={stats.total}
              mantine="green"
            />
            <StatChip
              icon={<LuShip size={15} />}
              label={t('page.occupancy.map.stat_occupied')}
              value={stats.occupied + stats.reserved}
              total={stats.total}
              mantine="blue"
              sub={`${occupancyPct}% occupancy`}
            />
            <StatChip
              icon={<LuCalendar size={15} />}
              label={t('page.occupancy.map.stat_reserved')}
              value={stats.reserved}
              total={stats.total}
              mantine="teal"
            />
            <StatChip
              icon={<LuWrench size={15} />}
              label={t('page.occupancy.map.stat_maintenance')}
              value={stats.maintenance}
              total={stats.total}
              mantine="gray"
            />
          </SimpleGrid>
        )}

        {isLoading && (
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={68} radius="md" />
            ))}
          </SimpleGrid>
        )}

        {/* Controls */}
        <Group gap="sm" justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            {/* Map / Timeline toggle */}
            <Group gap={4}>
              {[
                { label: 'Map', to: '/dashboard/occupancy/map', icon: <LuMap size={13} /> },
                { label: 'Timeline', to: '/dashboard/occupancy/calendar', icon: <LuCalendar size={13} /> },
              ].map((item) => {
                const active = item.to === '/dashboard/occupancy/map'
                return (
                  <Button
                    key={item.to}
                    size="xs"
                    variant={active ? 'filled' : 'default'}
                    color={active ? 'laut' : undefined}
                    leftSection={item.icon}
                    onClick={() => !active && navigate({ to: item.to as any })}
                  >
                    {item.label}
                  </Button>
                )
              })}
            </Group>

            <TextInput type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} size="xs" w={160} />
          </Group>

          <Group gap="sm" wrap="wrap">
            <TextInput
              placeholder={t('page.occupancy.map.placeholder_berth')}
              leftSection={<LuSearch size={13} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              size="xs"
              w={180}
              styles={{ input: { paddingLeft: 30 } }}
            />
            <Select
              placeholder={t('page.occupancy.map.placeholder_pontoon')}
              clearable
              data={pontoons.map((p) => ({ value: p.id, label: `${p.code}${p.name ? ` – ${p.name}` : ''}` }))}
              value={pontoonId}
              onChange={setPontoonId}
              size="xs"
              w={200}
            />
          </Group>
        </Group>

        {/* Edit mode hint */}
        {editMode && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 8,
              background: 'light-dark(var(--mantine-color-karang-0), rgba(224,120,86,0.1))',
            }}
          >
            <Text size="xs" c="karang.7" fw={500}>
              Mode edit aktif — drag berth untuk mengubah posisi. Perubahan disimpan otomatis setelah{' '}
              {SAVE_DEBOUNCE_MS / 1000}s tanpa aktivitas.
            </Text>
          </Box>
        )}

        {/* Map + legend */}
        <Card radius="md" padding={0} style={{ overflow: 'hidden' }}>
          {isLoading ? (
            <Skeleton height={480} radius={0} />
          ) : (
            <BerthMap berths={berths} editMode={editMode} onPositionChange={handlePosition} />
          )}

          {/* Legend */}
          <Group
            gap="lg"
            px="md"
            py="xs"
            wrap="wrap"
            style={{
              borderTop: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
            }}
          >
            {STATUS_META.map((s) => (
              <Group key={s.status} gap={6} wrap="nowrap">
                <Box w={10} h={10} style={{ borderRadius: 3, background: s.color, flexShrink: 0 }} />
                <Text size="xs" c="dimmed">
                  {t(`status.berth.${s.status}`)}
                </Text>
              </Group>
            ))}
            {editMode && (
              <Group gap={6} wrap="nowrap">
                <Box w={10} h={10} style={{ borderRadius: 3, background: '#FD7E14', flexShrink: 0 }} />
                <Text size="xs" c="dimmed">
                  Sedang diedit
                </Text>
              </Group>
            )}
            {berths.length > 0 && (
              <Text size="xs" c="dimmed" style={{ marginLeft: 'auto' }}>
                {berths.length} berth ditampilkan
              </Text>
            )}
          </Group>
        </Card>
      </Stack>
    </Container>
  )
}

function StatChip({
  icon,
  label,
  value,
  total,
  mantine,
  sub,
}: {
  icon: React.ReactNode
  label: string
  value: number
  total: number
  mantine: string
  sub?: string
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <Card radius="md" padding="sm">
      <Group gap="xs" mb={4} wrap="nowrap">
        <ThemeIcon variant="light" color={mantine} size="sm" radius="sm" style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Text size="xs" c="dimmed" truncate>
          {label}
        </Text>
        <Badge size="xs" variant="light" color={mantine} style={{ marginLeft: 'auto', flexShrink: 0 }}>
          {pct}%
        </Badge>
      </Group>
      <Text fw={700} size="lg" lh={1.1}>
        {value}
        <Text span size="xs" c="dimmed" fw={400} ml={4}>
          / {total}
        </Text>
      </Text>
      {sub && (
        <Text size="xs" c="dimmed" mt={2}>
          {sub}
        </Text>
      )}
    </Card>
  )
}
